// Session-expired UX helper.
//
// Triggered from useServices.js when an /ars/** call returns 401. Shows a
// blocking, full-screen overlay with a clear message and a single button that
// clears the dead ARS session state and bounces the user back to their
// /validateAcct/{accountId}/{uniqueCode} link so they can re-MFA.
//
// Vanilla DOM on purpose: this needs to fire from inside an axios catch
// handler, which doesn't sit in any React tree. A vanilla overlay works the
// same in PWA and browser, and survives whatever in-progress React render is
// happening when the 401 lands.

let modalShown = false;

export const showSessionExpiredModal = () => {
    // Idempotent: a single page typically has several /ars/** calls in flight,
    // and ALL of them will 401 at the moment the token expires. We only want
    // to show one modal.
    if (modalShown) return;
    modalShown = true;

    const userID = localStorage.getItem('userID');
    const uniqueCode = localStorage.getItem('token');

    // Drop the dead session state up front so any in-flight retry, background
    // refetch, or stray render that reads ars_token gets `null` instead of an
    // expired token.
    try {
        localStorage.removeItem('ars_token');
        localStorage.removeItem('ars_token_expires_at');
        localStorage.removeItem('ars_token_duration_sec');
        localStorage.removeItem('fcm_token');
    } catch (e) { /* non-fatal */ }

    const overlay = document.createElement('div');
    overlay.style.cssText = [
        'position:fixed', 'inset:0', 'z-index:2147483647',
        'background:rgba(0,0,0,0.55)',
        'display:flex', 'align-items:center', 'justify-content:center',
        'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif'
    ].join(';');

    const card = document.createElement('div');
    card.style.cssText = [
        'background:#fff', 'border-radius:8px',
        'padding:24px 28px', 'max-width:360px', 'width:90%',
        'box-shadow:0 10px 40px rgba(0,0,0,0.25)',
        'text-align:center'
    ].join(';');

    const title = document.createElement('h3');
    title.textContent = 'Session expired';
    title.style.cssText = 'margin:0 0 12px;font-size:18px;color:#222';

    const msg = document.createElement('p');
    if (userID && uniqueCode) {
        msg.textContent = 'Your session has expired. Please re-validate to continue.';
    } else {
        msg.textContent = 'Your session has expired. Please open your validation link again to continue.';
    }
    msg.style.cssText = 'margin:0 0 20px;font-size:14px;color:#555;line-height:1.4';

    const btn = document.createElement('button');
    btn.textContent = (userID && uniqueCode) ? 'Re-validate' : 'OK';
    btn.style.cssText = [
        'background:#0a66c2', 'color:#fff', 'border:0',
        'padding:10px 20px', 'border-radius:6px',
        'font-size:14px', 'font-weight:600', 'cursor:pointer'
    ].join(';');

    btn.addEventListener('click', () => {
        if (userID && uniqueCode) {
            const currentPath = window.location.pathname + window.location.search;
            window.location.href =
                `/validateAcct/${userID}/${uniqueCode}?returnUrl=${encodeURIComponent(currentPath)}`;
        } else {
            // No link to send them to — close the modal and let them figure
            // out the next step. They'll need the original email link.
            overlay.remove();
            modalShown = false;
        }
    });

    card.appendChild(title);
    card.appendChild(msg);
    card.appendChild(btn);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
};
